
// Phase 3: Offline CRDT sync indicator.
// Tracks online/offline status + a local outbox queue (mutations made while
// offline). When online, it pushes the queue to the server; when offline it
// shows a pending count. Clicking retries sync.
import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CloudOff, RefreshCw, Check, Wifi } from "lucide-react";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";

interface OutboxEntry {
  clientOpId: string;
  entity: string;
  op: string;
  payload: unknown;
}

const OUTBOX_KEY = "horndeliver.syncOutbox";

function readOutbox(): OutboxEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(OUTBOX_KEY);
    return raw ? (JSON.parse(raw) as OutboxEntry[]) : [];
  } catch {
    return [];
  }
}

function writeOutbox(entries: OutboxEntry[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(OUTBOX_KEY, JSON.stringify(entries));
    // Notify other listeners (same tab).
    window.dispatchEvent(new CustomEvent("horndeliver:outbox-change"));
  } catch {}
}

// Public helper used by views to enqueue a mutation when offline.
export function enqueueSyncOp(entry: OutboxEntry) {
  const current = readOutbox();
  if (current.some((e) => e.clientOpId === entry.clientOpId)) return;
  writeOutbox([...current, entry]);
}

export function SyncIndicator() {
  const { t } = useTranslation();
  const [online, setOnline] = useState(true);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refreshPending = useCallback(() => {
    setPending(readOutbox().length);
  }, []);

  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      // attempt drain shortly after regaining connectivity
      setTimeout(drain, 500);
    };
    const onOffline = () => setOnline(false);
    const onOutboxChange = () => refreshPending();
    setOnline(navigator.onLine);
    refreshPending();
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("horndeliver:outbox-change", onOutboxChange);
    // Poll pending every 5s as a backstop.
    const interval = setInterval(refreshPending, 5000);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("horndeliver:outbox-change", onOutboxChange);
      clearInterval(interval);
    };
  }, []);

  const drain = useCallback(async () => {
    const entries = readOutbox();
    if (entries.length === 0 || !navigator.onLine) return;
    setSyncing(true);
    try {
      const res = await api.sync.push(entries);
      if (res.ok) {
        // Remove successfully-applied ops (the server returns applied clientOpIds).
        const applied = new Set<string>(res.data?.applied ?? []);
        const remaining = entries.filter((e) => !applied.has(e.clientOpId));
        writeOutbox(remaining);
      }
    } catch {
      // leave queue intact; will retry
    } finally {
      setSyncing(false);
      refreshPending();
    }
  }, [refreshPending]);

  // Auto-drain when going online or when pending > 0 and online.
  useEffect(() => {
    if (online && pending > 0 && !syncing) {
      const id = setTimeout(drain, 800);
      return () => clearTimeout(id);
    }
  }, [online, pending, syncing, drain]);

  const label = !online
    ? t("sync.offline")
    : syncing
      ? t("sync.syncing")
      : pending > 0
        ? t("sync.pending", { count: pending })
        : t("sync.synced");

  const Icon = !online ? CloudOff : syncing ? RefreshCw : pending > 0 ? CloudOff : Check;
  const color = !online || pending > 0 ? "text-amber-600" : "text-emerald-600";

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => drain()}
            className={`h-9 gap-1.5 px-2 ${color} ${syncing ? "animate-pulse" : ""}`}
            aria-label={label}
            disabled={syncing || (!online && pending === 0)}
          >
            <Icon className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
            <span className="hidden text-[11px] font-semibold sm:inline">{label}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="text-xs">
            {!online ? (
              t("sync.offline")
            ) : pending > 0 ? (
              t("sync.pending", { count: pending })
            ) : (
              <>
                <Wifi className="mr-1 inline h-3 w-3" />
                {t("sync.crdtEnabled")}
              </>
            )}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
